import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '@/lib/api'

export type NotificationAction = 'subscribe' | 'unsubscribe' | 'blocked' | 'unsupported' | null

interface NotificationState {
  isSupported: boolean
  permission: NotificationPermission | null
  isSubscribed: boolean
  unreadCount: number
  newLeadsCount: number
}

export function useNotifications() {
  const [state, setState] = useState<NotificationState>({
    isSupported: false,
    permission: null,
    isSubscribed: false,
    unreadCount: 0,
    newLeadsCount: 0,
  })

  const [actionResult, setActionResult] = useState<{
    action: NotificationAction
    success: boolean
  } | null>(null)

  const initialized = useRef(false)

  // Initialize: check support, permission, and existing subscription
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    const init = async () => {
      const isSupported = 'serviceWorker' in navigator && 'PushManager' in window
      const permission = isSupported ? Notification.permission : null

      let isSubscribed = false

      if (isSupported && permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.register('/service-worker.js')
          await navigator.serviceWorker.ready
          const existingSub = await registration.pushManager.getSubscription()
          isSubscribed = !!existingSub
        } catch (err) {
          console.error('[Push] Init error:', err)
        }
      }

      setState({ isSupported, permission, isSubscribed, unreadCount: 0, newLeadsCount: 0 })
    }

    init()
  }, [])

  // Subscribe
  const subscribe = useCallback(async (): Promise<boolean> => {
    console.log('[Push] subscribe() called')
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window

    if (!isSupported) {
      console.log('[Push] Not supported')
      setActionResult({ action: 'unsupported', success: false })
      return false
    }

    const permission = await Notification.requestPermission()
    console.log('[Push] Permission:', permission)

    if (permission !== 'granted') {
      setState(prev => ({ ...prev, permission }))
      setActionResult({ action: 'blocked', success: false })
      return false
    }

    try {
      console.log('[Push] Registering service worker...')
      await navigator.serviceWorker.register('/service-worker.js')
      const registration = await navigator.serviceWorker.ready
      console.log('[Push] SW ready:', registration.scope)

      console.log('[Push] Fetching VAPID key...')
      const { data } = await api.get('/notifications/vapid-public-key')
      const vapidPublicKey = data.data.publicKey
      console.log('[Push] VAPID key received:', vapidPublicKey ? 'yes' : 'no')

      if (!vapidPublicKey) {
        setActionResult({ action: 'subscribe', success: false })
        return false
      }

      console.log('[Push] Subscribing to push...')
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as any,
      })
      console.log('[Push] Push subscription created:', subscription.endpoint)

      console.log('[Push] Sending subscription to server...')
      await api.post('/notifications/subscribe', {
        subscription: subscription.toJSON(),
      })
      console.log('[Push] Server saved subscription')

      setState(prev => ({ ...prev, permission: 'granted', isSubscribed: true }))
      setActionResult({ action: 'subscribe', success: true })
      console.log('[Push] subscribe() SUCCESS')
      return true
    } catch (error: any) {
      console.error('[Push] Subscription failed:', error.message, error.response?.data)
      setActionResult({ action: 'subscribe', success: false })
      return false
    }
  }, [])

  // Unsubscribe
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    console.log('[Push] unsubscribe() called')
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window
    if (!isSupported) return false

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      console.log('[Push] Existing subscription:', subscription ? 'yes' : 'no')

      if (subscription) {
        await subscription.unsubscribe()
        console.log('[Push] Unsubscribed from push manager')
        await api.post('/notifications/unsubscribe', {
          endpoint: subscription.endpoint,
        })
        console.log('[Push] Server removed subscription')
      }

      setState(prev => ({ ...prev, isSubscribed: false }))
      setActionResult({ action: 'unsubscribe', success: true })
      console.log('[Push] unsubscribe() SUCCESS')
      return true
    } catch (error: any) {
      console.error('[Push] Unsubscribe failed:', error.message)
      setActionResult({ action: 'unsubscribe', success: false })
      return false
    }
  }, [])

  const resetActionResult = useCallback(() => {
    setActionResult(null)
  }, [])

  // Fetch unread count + new leads count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const [conversationsRes, leadsRes] = await Promise.all([
        api.get('/conversations/unread-count'),
        api.get('/leads/new-count'),
      ])
      setState(prev => ({
        ...prev,
        unreadCount: conversationsRes.data.data.unreadCount || 0,
        newLeadsCount: leadsRes.data.data.newCount || 0,
      }))
    } catch (error) {
      console.error('[Notifications] Failed to fetch unread count:', error)
    }
  }, [])

  // Mark conversation as viewed
  const markAsViewed = useCallback(async (conversationId: string) => {
    try {
      await api.post(`/conversations/${conversationId}/view`)
      fetchUnreadCount()
    } catch (error) {
      console.error('[Notifications] Failed to mark as viewed:', error)
    }
  }, [fetchUnreadCount])

  // Mark all leads as viewed
  const markLeadsAsViewed = useCallback(async () => {
    try {
      await api.post('/leads/mark-all-viewed')
      fetchUnreadCount()
    } catch (error) {
      console.error('[Notifications] Failed to mark leads as viewed:', error)
    }
  }, [fetchUnreadCount])

  // Poll unread count periodically + refresh immediately when requested
  // (e.g. after ConversationDetail marks a conversation as viewed)
  useEffect(() => {
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000)
    const refresh = () => fetchUnreadCount()
    window.addEventListener('r3c:unread-count-refresh', refresh)
    return () => {
      clearInterval(interval)
      window.removeEventListener('r3c:unread-count-refresh', refresh)
    }
  }, [fetchUnreadCount])

  return {
    ...state,
    actionResult,
    subscribe,
    unsubscribe,
    resetActionResult,
    fetchUnreadCount,
    markAsViewed,
    markLeadsAsViewed,
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
